import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Info,
  Moon,
  Sun,
  LogOut,
  LayoutDashboard,
  ArrowUpRight,
  Receipt,
  ShoppingCart,
  Target,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  FileText,
  Users,
  LifeBuoy,
  BookOpen,
  FolderOpen,
  Video,
  UserCog,
  CalendarDays,
  Lightbulb,
  Lock,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { usePlanFeatures } from "@/hooks/use-plan-features";
import { usePlanNamesWithFeature } from "@/hooks/use-public-plans";
import { useUnreadTicketCount } from "@/hooks/use-support";
import {
  FIRST_ACCESS_TUTORIAL_STEPS,
  useActiveOnboardingStep,
} from "@/components/onboarding/tutorial-runtime";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface AppSidebarProps {
  onLogout: () => void;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  user: {
    name?: string;
    email?: string;
    avatar_url?: string;
  } | null;
}

// Definição de todas as funcionalidades disponíveis
const allNavigationGroups = [
  {
    title: "Dashboard",
    collapsible: false,
    featureRequired: null,
    items: [{ id: "financial-dashboard", title: "Dashboard", icon: LayoutDashboard, path: "/financial", featureRequired: "dashboard-financeiro" }],
  },
  {
    title: "Minhas Finanças",
    collapsible: true,
    featureRequired: null,
    items: [
      { id: "revenues", title: "Receitas", icon: ArrowUpRight, path: "/financial/revenues", featureRequired: "gestao-de-receitas" },
      { id: "future-expenses", title: "Despesas Futuras", icon: Target, path: "/financial/future-expenses", featureRequired: "despesas-futuras" },
      { id: "monthly-expenses", title: "Despesas Mensais", icon: Receipt, path: "/financial/monthly-expenses", featureRequired: "despesas-mensais" },
      { id: "daily-expenses", title: "Despesas do Dia a Dia", icon: ShoppingCart, path: "/financial/daily-expenses", featureRequired: "despesas-do-dia-a-dia" },
      { id: "reports", title: "Relatórios", icon: FileText, path: "/financial/reports", featureRequired: "relatorios-financeiros" },
    ],
  },
  {
    title: "Aprendizado",
    collapsible: true,
    featureRequired: null,
    items: [
      { id: "materials", title: "Materiais de Apoio", icon: FolderOpen, path: "/learn/materials", featureRequired: "materiais-de-apoio" },
      { id: "videos", title: "Vídeo-aulas", icon: Video, path: "/learn/videos", featureRequired: "video-aulas" },
      { id: "blog", title: "Blog", icon: BookOpen, path: "/learn/blog", featureRequired: "blog" },
    ],
  },
  {
    title: "Conta",
    collapsible: true,
    featureRequired: null,
    items: [
      { id: "my-account", title: "Minha Conta", icon: UserCog, path: "/account", featureRequired: "minha-conta" },
      { id: "mentoria", title: "Mentoria Librattum", icon: Lightbulb, path: "/account/mentoria", featureRequired: "mentoria-librattum" },
      { id: "support", title: "Suporte", icon: LifeBuoy, path: "/support", featureRequired: "suporte" },
      { id: "ajuda", title: "Central de ajuda", icon: Info, path: "/support/help", featureRequired: null },
    ],
  },
  {
    title: "Configurações",
    collapsible: true,
    featureRequired: null,
    items: [
      { id: "agents", title: "Agentes", icon: Users, path: "/financial/agents", featureRequired: "agentes" },
      { id: "budget-period", title: "Período Orçamentário", icon: CalendarDays, path: "/account/budget-period", featureRequired: "minha-conta" },
    ],
  },
];

// Por padrão apenas "Minhas Finanças" começa expandida; as demais seções
// colapsáveis vêm recolhidas e só abrem/fecham por ação do usuário.
const DEFAULT_OPEN_GROUP = "Minhas Finanças";

const buildInitialCollapsedGroups = (): Record<string, boolean> =>
  Object.fromEntries(
    allNavigationGroups
      .filter((group) => group.collapsible && group.title !== DEFAULT_OPEN_GROUP)
      .map((group) => [group.title, true])
  );

const tutorialStepByItemId: Partial<Record<string, string>> = {
  "reports": FIRST_ACCESS_TUTORIAL_STEPS.reportsNav,
  "my-account": FIRST_ACCESS_TUTORIAL_STEPS.myAccountNav,
  "agents": FIRST_ACCESS_TUTORIAL_STEPS.agentNav,
  "budget-period": FIRST_ACCESS_TUTORIAL_STEPS.budgetPeriodNav,
  "support": FIRST_ACCESS_TUTORIAL_STEPS.supportNav,
  "ajuda": FIRST_ACCESS_TUTORIAL_STEPS.helpCenterNav,
};

/**
 * Grupo colapsável em que cada item do tutorial vive. Itens dentro de um grupo
 * recolhido não existem no DOM, e o tour ficaria sem alvo para destacar.
 */
const tutorialGroupByStep: Record<string, string> = Object.fromEntries(
  allNavigationGroups.flatMap((group) =>
    group.collapsible
      ? group.items
          .map((item) => [tutorialStepByItemId[item.id], group.title] as const)
          .filter(([step]) => Boolean(step))
      : []
  )
) as Record<string, string>;

const tutorialNavIds: Record<string, string> = {
  "financial-dashboard": "nav-dashboard",
  "reports": "nav-reports",
  "my-account": "nav-my-account",
  "agents": "nav-agents",
  "budget-period": "nav-budget-period",
  "mentoria": "nav-mentoria",
  "support": "nav-support",
  "ajuda": "nav-help-center",
  "revenues": "nav-revenues",
  "future-expenses": "nav-future-expenses",
  "monthly-expenses": "nav-monthly-expenses",
  "daily-expenses": "nav-daily-expenses",
};

const formatNotificationCount = (count: number) => (count > 99 ? "99+" : String(count));

function SupportNotificationBadge({
  count,
  className,
}: Readonly<{
  count: number;
  className?: string;
}>) {
  if (count <= 0) return null;

  return (
    <span
      className={cn(
        "inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-semibold leading-none text-white shadow-sm",
        className
      )}
    >
      {formatNotificationCount(count)}
    </span>
  );
}

const AppSidebar = ({ onLogout, darkMode, onToggleDarkMode, user }: AppSidebarProps) => {
  const { state, toggleSidebar, isMobile } = useSidebar();
  const location = useLocation();
  const activeTutorialStep = useActiveOnboardingStep();
  const isCollapsed = state === "collapsed";
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(buildInitialCollapsedGroups);
  const [blockedModalOpen, setBlockedModalOpen] = useState(false);
  const [blockedItemTitle, setBlockedItemTitle] = useState("");
  const [blockedItemFeature, setBlockedItemFeature] = useState<string | null>(null);
  const { hasFeature, subscription } = usePlanFeatures();
  // Planos reais (de /public/plans) que incluem a funcionalidade bloqueada —
  // nunca sugerir nomes de planos fixos no código.
  const blockedFeaturePlanNames = usePlanNamesWithFeature(blockedItemFeature);
  const { data: unreadSupportData } = useUnreadTicketCount();
  const unreadSupportCount = unreadSupportData?.unread_count ?? 0;

  // Abre sozinho o grupo do menu que contém o item apontado pelo tutorial.
  useEffect(() => {
    if (!activeTutorialStep) return;

    const groupTitle = tutorialGroupByStep[activeTutorialStep];
    if (!groupTitle) return;

    setCollapsedGroups((prev) => (prev[groupTitle] ? { ...prev, [groupTitle]: false } : prev));
  }, [activeTutorialStep]);

  const toggleGroup = (title: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [title]: !(prev[title] ?? false) }));
  };

  // Função para lidar com clique em menu bloqueado
  const handleBlockedClick = (
    itemTitle: string,
    featureSlug: string | null,
    e: React.MouseEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setBlockedItemTitle(itemTitle);
    setBlockedItemFeature(featureSlug);
    setBlockedModalOpen(true);
  };

  // Verificar se usuário tem acesso a uma feature
  const checkAccess = (featureSlug: string | null): { hasAccess: boolean; isLocked: boolean } => {
    if (!featureSlug) return { hasAccess: true, isLocked: false };
    const hasAccess = hasFeature(featureSlug);
    return { hasAccess, isLocked: !hasAccess };
  };

  return (
    <>
      <Sidebar 
        className="border-r border-sidebar-border bg-sidebar-background"
        collapsible="icon"
      >
        {/* Header */}
        <SidebarHeader className="p-4">
          <div className="flex items-center justify-between">
            {!isCollapsed ? (
              <>
                <div className="flex items-center gap-3">
                  <img
                    src="/logo-librattum.png"
                    alt="Librattum"
                    className="h-8"
                  />
                </div>
                {!isMobile && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleSidebar}
                    className="h-8 w-8 text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-border"
                  >
                    <PanelLeftClose className="h-4 w-4" />
                  </Button>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center w-full">
                {!isMobile && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleSidebar}
                    className="h-10 w-10 text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-border"
                  >
                    <PanelLeft className="h-5 w-5" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </SidebarHeader>

        <Separator className="bg-sidebar-border" />

        {/* Navigation */}
        <SidebarContent className="px-3 py-4">
          {allNavigationGroups.map((group, groupIndex) => {
            const isGroupCollapsed = group.collapsible
              ? (collapsedGroups[group.title] ?? false)
              : false;

            return (
              <SidebarGroup key={group.title} className={cn("py-1", groupIndex > 0 && "mt-1")}>
                {!isCollapsed && group.collapsible && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.title)}
                    aria-expanded={!isGroupCollapsed}
                    aria-label={`${isGroupCollapsed ? "Expandir" : "Recolher"} ${group.title}`}
                    className="flex w-[calc(100%+1rem)] -mx-2 items-center gap-2 rounded-md bg-sidebar-border/40 px-3 py-3 mb-2 text-left transition-colors hover:bg-sidebar-border/60"
                  >
                    <ChevronRight
                      className={cn(
                        "h-3.5 w-3.5 shrink-0 text-sidebar-primary transition-transform duration-200",
                        !isGroupCollapsed && "rotate-90"
                      )}
                    />
                    <span className="text-xs font-bold uppercase tracking-wider text-sidebar-primary">
                      {group.title}
                    </span>
                  </button>
                )}
                <SidebarGroupContent>
                  {!isGroupCollapsed && (
                    <SidebarMenu className="space-y-1">
                      {group.items.map((item) => {
                        const { isLocked } = checkAccess(item.featureRequired);
                        const isActive = location.pathname === item.path || 
                          (item.path === "/financial" && location.pathname === "/dashboard");
                        const showSupportBadge = item.id === "support" && unreadSupportCount > 0;
                        const isTutorialHighlighted =
                          tutorialStepByItemId[item.id] === activeTutorialStep;
                        const isEmphasized = isActive || isTutorialHighlighted;
                        
                        return (
                          <SidebarMenuItem key={item.id}>
                            {item.path.startsWith("http") ? (
                              <a 
                                href={item.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(
                                  "w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150",
                                  "text-sidebar-muted-foreground hover:bg-sidebar-border hover:text-sidebar-foreground cursor-pointer"
                                )}
                              >
                                <item.icon className="h-4 w-4 shrink-0" />
                                {!isCollapsed && <span>{item.title}</span>}
                              </a>
                            ) : (
                              <div 
                                data-tour-id={tutorialNavIds[item.id]}
                                className={cn(
                                  "w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150",
                                  isEmphasized
                                    ? "bg-sidebar-primary text-sidebar-primary-foreground shadow-sm" 
                                    : "text-sidebar-muted-foreground hover:bg-sidebar-border hover:text-sidebar-foreground",
                                  isLocked && !isEmphasized && "opacity-70"
                                )}
                              >
                                <NavLink 
                                  to={isLocked ? "#" : item.path}
                                  onClick={(e) =>
                                    isLocked
                                      ? handleBlockedClick(item.title, item.featureRequired, e)
                                      : undefined
                                  }
                                  className="flex-1 flex items-center gap-3 min-w-0"
                                >
                                  <div className="relative shrink-0">
                                    <item.icon className={cn("h-4 w-4 shrink-0", isEmphasized && "text-sidebar-primary-foreground")} />
                                    {isCollapsed && showSupportBadge && (
                                      <SupportNotificationBadge
                                        count={unreadSupportCount}
                                        className="absolute -right-2 -top-2 h-4 min-w-4 px-1 text-[9px]"
                                      />
                                    )}
                                  </div>
                                  {!isCollapsed && (
                                    <span className={cn(
                                      "truncate",
                                      item.id === "daily-expenses" && "text-xs"
                                    )}>
                                      {item.title}
                                    </span>
                                  )}
                                </NavLink>
                                {!isCollapsed && showSupportBadge && (
                                  <SupportNotificationBadge count={unreadSupportCount} />
                                )}
                                {!isCollapsed && isLocked && (
                                  <Lock className="h-3 w-3 text-amber-500 flex-shrink-0" />
                                )}
                                {!isCollapsed && isEmphasized && (
                                  <ChevronRight className="ml-auto h-4 w-4 shrink-0 opacity-60" />
                                )}
                              </div>
                            )}
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  )}
                </SidebarGroupContent>
              </SidebarGroup>
            );
          })}
        </SidebarContent>

        {/* Footer */}
        <SidebarFooter className="p-3 mt-auto">
          <Separator className="bg-sidebar-border mb-3" />
          
          {/* User info */}
          {!isCollapsed && (
            <div className="flex items-center gap-3 px-2 py-2 rounded-lg bg-sidebar-border/50 mb-3">
              <Avatar className="h-9 w-9 border-2 border-sidebar-border">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                  {user?.name?.charAt(0)?.toUpperCase() ?? "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">
                  {user?.name ?? "Usuário"}
                </p>
                <p className="text-xs text-sidebar-muted-foreground truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          )}

          {/* User avatar when collapsed */}
          {isCollapsed && (
            <div className="flex justify-center mb-3">
              <Avatar className="h-9 w-9 border-2 border-sidebar-border">
                <AvatarImage src={user?.avatar_url} />
                <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
                  {user?.name?.charAt(0)?.toUpperCase() ?? "U"}
                </AvatarFallback>
              </Avatar>
            </div>
          )}

          {/* Actions */}
          <div className={cn("flex gap-2", isCollapsed ? "flex-col" : "")}>
            <Button
              variant="ghost"
              size={isCollapsed ? "icon" : "sm"}
              onClick={onToggleDarkMode}
              className={cn(
                "text-sidebar-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-border",
                isCollapsed ? "w-full" : "flex-1"
              )}
              title={darkMode ? "Modo claro" : "Modo escuro"}
            >
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {!isCollapsed && <span className="ml-2">{darkMode ? "Claro" : "Escuro"}</span>}
            </Button>
            <Button
              variant="ghost"
              size={isCollapsed ? "icon" : "sm"}
              onClick={onLogout}
              className={cn(
                "text-rose-400 hover:text-rose-300 hover:bg-rose-500/10",
                isCollapsed ? "w-full" : "flex-1"
              )}
              title="Sair"
            >
              <LogOut className="h-4 w-4" />
              {!isCollapsed && <span className="ml-2">Sair</span>}
            </Button>
          </div>
        </SidebarFooter>
      </Sidebar>

      {/* Modal de funcionalidade bloqueada */}
      <Dialog open={blockedModalOpen} onOpenChange={setBlockedModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-amber-100">
                <Lock className="h-5 w-5 text-amber-600" />
              </div>
              <DialogTitle>Funcionalidade Bloqueada</DialogTitle>
            </div>
            <DialogDescription className="pt-4">
              A funcionalidade <strong className="text-foreground">{blockedItemTitle}</strong> não está disponível no seu plano atual.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-muted rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Seu plano atual:</span>
                <span className="font-medium">{subscription?.plan_name || "Sem plano ativo"}</span>
              </div>
              {blockedFeaturePlanNames.length > 0 && (
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">Disponível em:</span>
                  <span className="font-medium text-amber-600 text-right">
                    {blockedFeaturePlanNames.join(", ")}
                  </span>
                </div>
              )}
            </div>
            
            <p className="text-sm text-muted-foreground mt-4">
              Atualize seu plano para desbloquear esta e outras funcionalidades exclusivas.
            </p>
          </div>

          <DialogFooter className="sm:justify-between gap-3">
            <Button
              variant="outline"
              onClick={() => setBlockedModalOpen(false)}
              className="flex-1"
            >
              Fechar
            </Button>
            <Button
              onClick={() => {
                setBlockedModalOpen(false);
                window.location.href = "/account?tab=plan";
              }}
              className="flex-1"
            >
              Ver Meus Planos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AppSidebar;
